//
//  HelpTopicSectionsField.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the ordered section editor of the help topic form.
//

import { ActionIcon, Box, Group, Paper, Switch, Text, Title, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
    Collection,
    FormReadonlyProvider,
    Scope,
    useArrayFieldActions,
    useArrayFieldItemIndex,
    useFieldActions,
    useFieldDisabled,
    useFieldSelector,
    useFieldSnapshot,
    useStableCallback,
    type ArrayFieldActions,
    type FieldActions
} from "@ngt-form/core";
import { GroupedFieldsProvider, SelectInput, TextInput } from "@ngt-form/mantine";
import { memo, type FC } from "react";

import AppSettings from "src/AppSettings";
import type { HelpTopic, HelpTopicSection, LookupOption } from "src/api/Tmd.dtos";
import Icon from "src/components/common/Icon";
import ConfigurationInsertionControl from "src/components/forms/fields/ConfigurationInsertionControl";
import LongGroupedField from "src/components/forms/fields/grouped/LongGroupedField";
import RichTextEditorInput from "src/components/forms/inputs/RichTextEditorInput";
import type { TMDFormSubmitMetadata } from "src/components/forms/TMDFormSubmitButtons";
import type { TMDValidationError } from "src/types/TMDValidationError";

type SetSections = FieldActions<
    HelpTopic,
    TMDValidationError,
    TMDFormSubmitMetadata,
    HelpTopicSection[],
    LookupOption
>["setValue"];

type SectionActions = ArrayFieldActions<
    HelpTopic,
    TMDValidationError,
    TMDFormSubmitMetadata,
    HelpTopicSection,
    LookupOption
>;

/** Supplies the collection actions and presentation options shared by every section row. */
type HelpTopicSectionItemProps = {
    collectionActions: SectionActions;
    count: number;
    showInactive: boolean;
};

/**
 * Requests insertion of a new active section at the index.
 *
 * Postconditions: The draft gains a section at the clamped index; the server assigns ranks from
 * the saved order. The action notifies and marks the form dirty; nothing is persisted.
 */
const insertSection = (setSections: SetSections, index: number) =>
    setSections(
        current => {
            const sections = [...(current ?? [])];
            sections.splice(Math.max(0, Math.min(sections.length, index)), 0, {
                active: true,
                content: ""
            } as HelpTopicSection);
            return sections;
        },
        { notify: true, updateDirty: true }
    );

/** Supplies the position of the section whose heading is rendered. */
type HelpTopicSectionHeadingProps = {
    index: number;
};

/**
 * Renders a section's heading: its title, or its position when it has none.
 *
 * Preconditions: Rendered within a sections collection row.
 * Postconditions: Subscribes to the row's title alone, so typing a title renders only the heading.
 */
const HelpTopicSectionHeading: FC<HelpTopicSectionHeadingProps> = ({ index }) => {
    const { value: title } = useFieldSnapshot<
        HelpTopic,
        TMDValidationError,
        TMDFormSubmitMetadata,
        string
    >(
        { value: true },
        { scope: Scope.PropertyPath, propertyPath: "title", prependParentPropertyPath: true }
    );

    return <Title order={5}>{title || `Section ${index + 1}`}</Title>;
};

/**
 * Renders one section's controls and fields, hiding inactive sections unless requested.
 *
 * Preconditions: Rendered by the sections collection, which scopes property paths to the item.
 * Postconditions: Persisted sections are deactivated or restored; unsaved sections are removed.
 * Inactive and disabled sections are read-only. All actions update the draft only. Memoised, and
 * the heading reads the title, so typing in one section renders no other section's card.
 */
const HelpTopicSectionItem: FC<HelpTopicSectionItemProps> = memo(
    ({ collectionActions, count, showInactive }) => {
        const index = useArrayFieldItemIndex();
        const disabled = useFieldDisabled();
        const { value: id } = useFieldSnapshot<
            HelpTopic,
            TMDValidationError,
            TMDFormSubmitMetadata,
            number
        >(
            { value: true },
            { scope: Scope.PropertyPath, propertyPath: "id", prependParentPropertyPath: true }
        );
        const { value: activeValue } = useFieldSnapshot<
            HelpTopic,
            TMDValidationError,
            TMDFormSubmitMetadata,
            boolean
        >(
            { value: true },
            { scope: Scope.PropertyPath, propertyPath: "active", prependParentPropertyPath: true }
        );
        const { setValue: setActive } = useFieldActions<
            HelpTopic,
            TMDValidationError,
            TMDFormSubmitMetadata,
            boolean,
            LookupOption
        >({ scope: Scope.PropertyPath, propertyPath: "active", prependParentPropertyPath: true });
        const { setValue: setSections } = useFieldActions<
            HelpTopic,
            TMDValidationError,
            TMDFormSubmitMetadata,
            HelpTopicSection[],
            LookupOption
        >({
            scope: Scope.PropertyPath,
            propertyPath: "sections",
            prependParentPropertyPath: false
        });

        const active = activeValue !== false;
        const persisted = (id ?? 0) > 0;

        if (!active && !showInactive) {
            return null;
        }

        return (
            <>
                {index === 0 && (
                    <ConfigurationInsertionControl
                        ariaLabel="Add help section here"
                        disabled={disabled}
                        tooltip="Add help section here"
                        onClick={() => insertSection(setSections, 0)}
                    />
                )}
                <Box
                    m="sm"
                    opacity={active ? 1 : 0.4}
                >
                    <Group
                        justify="space-between"
                        align="flex-end"
                    >
                        <Paper
                            withBorder
                            py={2}
                            px="sm"
                            style={{
                                borderBottom: "none",
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0
                            }}
                        >
                            <HelpTopicSectionHeading index={index} />
                        </Paper>
                        <ActionIcon.Group>
                            <Tooltip label="Move up">
                                <ActionIcon
                                    variant="default"
                                    size="lg"
                                    aria-label="Move section up"
                                    style={{ borderBottom: "none", borderBottomLeftRadius: 0 }}
                                    disabled={disabled || index === 0}
                                    onClick={() =>
                                        collectionActions.move(index, index - 1, {
                                            notify: true,
                                            updateDirty: true
                                        })
                                    }
                                >
                                    <Icon icon={AppSettings.icons.arrowUp} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Move down">
                                <ActionIcon
                                    variant="default"
                                    size="lg"
                                    aria-label="Move section down"
                                    style={{ borderBottom: "none" }}
                                    disabled={disabled || index >= count - 1}
                                    onClick={() =>
                                        collectionActions.move(index, index + 1, {
                                            notify: true,
                                            updateDirty: true
                                        })
                                    }
                                >
                                    <Icon icon={AppSettings.icons.arrowDown} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={active ? "Remove" : "Restore"}>
                                <ActionIcon
                                    variant="default"
                                    size="lg"
                                    aria-label={active ? "Remove section" : "Restore section"}
                                    style={{ borderBottom: "none", borderBottomRightRadius: 0 }}
                                    disabled={disabled}
                                    onClick={
                                        persisted
                                            ? () =>
                                                  setActive(current => current === false, {
                                                      notify: true,
                                                      updateDirty: true
                                                  })
                                            : () =>
                                                  collectionActions.delete(index, {
                                                      notify: true,
                                                      updateDirty: true
                                                  })
                                    }
                                >
                                    <Icon
                                        icon={
                                            active
                                                ? AppSettings.icons.deactivate
                                                : AppSettings.icons.activate
                                        }
                                    />
                                </ActionIcon>
                            </Tooltip>
                        </ActionIcon.Group>
                    </Group>
                    <FormReadonlyProvider readonly={disabled || !active}>
                        <Paper
                            withBorder
                            style={{
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                                overflow: "hidden"
                            }}
                        >
                            <LongGroupedField
                                propertyPath="title"
                                formInputComponent={TextInput}
                            />
                            <LongGroupedField
                                propertyPath="requiredPermission"
                                formInputComponent={SelectInput}
                                description="Leave empty to show this section to everyone. Otherwise it is shown only to users who hold the permission for the trial or institution being viewed."
                                inputWrapperOrder={["label", "input", "description", "error"]}
                                placeholder="Everyone"
                                nothingFoundMessage="No results found..."
                                clearable
                                searchable
                            />
                            <LongGroupedField
                                propertyPath="content"
                                formInputComponent={RichTextEditorInput}
                            />
                        </Paper>
                    </FormReadonlyProvider>
                </Box>
                <ConfigurationInsertionControl
                    ariaLabel="Add help section here"
                    disabled={disabled}
                    tooltip="Add help section here"
                    onClick={() => insertSection(setSections, index + 1)}
                />
            </>
        );
    }
);

/**
 * Hosts the ordered help sections with insertion, movement and inactive visibility controls.
 *
 * Preconditions: Mounted within the help topic form, whose root owns the sections array.
 * Postconditions: Section changes update the draft; saving the form persists them in order.
 */
const HelpTopicSectionsField: FC = () => {
    const disabled = useFieldDisabled();
    const [showInactive, showInactiveHandlers] = useDisclosure();
    const collectionActions = useArrayFieldActions<
        HelpTopic,
        TMDValidationError,
        TMDFormSubmitMetadata,
        HelpTopicSection,
        LookupOption
    >({ scope: Scope.PropertyPath, propertyPath: "sections", prependParentPropertyPath: false });
    const { setValue: setSections } = useFieldActions<
        HelpTopic,
        TMDValidationError,
        TMDFormSubmitMetadata,
        HelpTopicSection[],
        LookupOption
    >({ scope: Scope.PropertyPath, propertyPath: "sections", prependParentPropertyPath: false });
    // Selects only the count, so typing in a section does not render the collection.
    const count = useFieldSelector<
        HelpTopic,
        TMDValidationError,
        TMDFormSubmitMetadata,
        HelpTopicSection[],
        LookupOption,
        number
    >(
        useStableCallback(snapshot => snapshot.value?.length ?? 0),
        { value: true },
        { scope: Scope.PropertyPath, propertyPath: "sections", prependParentPropertyPath: false }
    );

    return (
        <Collection
            propertyPath="sections"
            header={
                <Group
                    justify="space-between"
                    align="center"
                    p="sm"
                    gap="sm"
                >
                    <Box>
                        <Title order={3}>Sections</Title>
                        <Text
                            size="sm"
                            c="dimmed"
                        >
                            Each section can be limited to users with a permission.
                        </Text>
                    </Box>
                    <Switch
                        label="Show removed sections"
                        checked={showInactive}
                        onChange={event => showInactiveHandlers.set(event.currentTarget.checked)}
                        labelPosition="left"
                    />
                </Group>
            }
            noItems={
                <ConfigurationInsertionControl
                    ariaLabel="Add help section here"
                    disabled={disabled}
                    tooltip="Add help section here"
                    onClick={() => insertSection(setSections, 0)}
                />
            }
        >
            <GroupedFieldsProvider groupedFields={false}>
                <HelpTopicSectionItem
                    collectionActions={collectionActions}
                    count={count}
                    showInactive={showInactive}
                />
            </GroupedFieldsProvider>
        </Collection>
    );
};

export default HelpTopicSectionsField;
